import * as React from 'react';
import { HighlightedItem } from './HighlightedItem';
import { HighlighterProps } from './Highlighter.types';
/**
 * Highlighter
 *
 * It highlights string type children by wrapping match
 * with strong dom element.
 * It remains children element structure.
 */

type ChildKeyGenerator = () => string;

const childKeyGenerator = (): ChildKeyGenerator => {
  let childKey = 0;
  return () => `highlighted-child-${childKey++}`;
};

const ELEM_TYPES = {
  STRING: 'string',
  ARRAY: 'array',
  REACT_ELEMENT: 'React_element',
} as const;

const getElementType = (element: React.ReactNode): string => {
  if (Array.isArray(element)) {
    return ELEM_TYPES.ARRAY;
  }
  if (React.isValidElement(element)) {
    return ELEM_TYPES.REACT_ELEMENT;
  }
  if (typeof element === 'string') {
    return ELEM_TYPES.STRING;
  }

  return '';
};

const highlight = (
  element: React.ReactNode,
  match: HighlighterProps['match'],
  nextChildKey: ChildKeyGenerator,
  props: HighlighterProps,
): React.ReactNode => {
  if (!element) {
    return null;
  }
  const elementType = getElementType(element);
  const elementTypesMap: Record<
    string,
    (elem: any, match: HighlighterProps['match']) => React.ReactNode
  > = {
    [ELEM_TYPES.STRING]: (elem, _match) => (
      <HighlightedItem
        key={nextChildKey()}
        match={_match}
        emphasize={props.emphasize}
      >
        {elem}
      </HighlightedItem>
    ),
    [ELEM_TYPES.REACT_ELEMENT]: elem => {
      if (elem.props.children) {
        return React.cloneElement(
          elem,
          { ...elem.props, key: nextChildKey() },
          highlight(elem.props.children, match, nextChildKey, props),
        );
      }
      return elem;
    },
    [ELEM_TYPES.ARRAY]: elem =>
      elem.map((el: React.ReactNode) =>
        highlight(el, match, nextChildKey, props),
      ),
  };

  return elementTypesMap[elementType]
    ? elementTypesMap[elementType](element, match)
    : element;
};

class Highlighter extends React.PureComponent<HighlighterProps> {
  static displayName = 'Highlighter';

  nextChildKey: ChildKeyGenerator;

  constructor(props: HighlighterProps) {
    super(props);
    // we want to create new react keys generator for instance of highlighter
    this.nextChildKey = childKeyGenerator();
  }

  render() {
    const { dataHook, children, match } = this.props;
    return (
      <span data-hook={dataHook}>
        {highlight(children, match, this.nextChildKey, this.props)}
      </span>
    );
  }
}

export { Highlighter };
